// Spreadsheet-style fill for Watchlist attribute columns.
//
// Fill always copies; it never extends a series. Attribute columns are
// categorical values, so "Tier 1" dragged down must stay
// "Tier 1" rather than becoming "Tier 2", "Tier 3", ... as Excel would make it.
// A multi-cell source repeats as a block (A, B, A, B, ...).

/**
 * The value a row receives when the source block starts at `sourceStart` in
 * display order. The block stays aligned to row positions in both directions,
 * so filling upwards continues it backwards (A, B written above A, B).
 */
export function fillValueAt(sourceValues, sourceStart, displayIndex) {
    const length = sourceValues.length;
    if (length === 0) return undefined;
    const offset = (((displayIndex - sourceStart) % length) + length) % length;
    return sourceValues[offset];
}

/**
 * Plan the cell writes for one fill.
 *
 * `rowIds` lists every visible row in display order (current sort and filter),
 * so rows the user cannot see are never written. `sourceRange` and
 * `targetRange` are inclusive display-index ranges; the target may sit above
 * or below the source. Target cells that are not editable are skipped without
 * shifting the pattern, so each row gets the value for its own position.
 */
export function planFill({
    rowIds,
    columnIds,
    sourceRange,
    targetRange,
    readValue,
    isEditable,
}) {
    const lastIndex = rowIds.length - 1;
    const sourceStart = Math.max(0, sourceRange.startIndex);
    const sourceEnd = Math.min(lastIndex, sourceRange.endIndex);
    const targetStart = Math.max(0, targetRange.startIndex);
    const targetEnd = Math.min(lastIndex, targetRange.endIndex);
    if (sourceStart > sourceEnd || targetStart > targetEnd) return [];

    const changes = [];
    columnIds.forEach((columnId) => {
        const sourceValues = [];
        for (let index = sourceStart; index <= sourceEnd; index += 1) {
            sourceValues.push(readValue(rowIds[index], columnId));
        }
        for (let index = targetStart; index <= targetEnd; index += 1) {
            if (index >= sourceStart && index <= sourceEnd) continue;
            const rowId = rowIds[index];
            if (!isEditable(rowId, columnId)) continue;
            changes.push({
                rowId,
                columnId,
                value: fillValueAt(sourceValues, sourceStart, index),
            });
        }
    });
    return changes;
}

/**
 * The target of a double-click on the fill handle: every visible row below
 * the source block, to the end of the list.
 */
export function fillToEndTarget(sourceRange, rowCount) {
    return { startIndex: sourceRange.endIndex + 1, endIndex: rowCount - 1 };
}
